/**
 * Document type → expected field mappings.
 * From REGULATORY_SEED_DATA.md §7.
 */

export interface DocTypeMapping {
  description: string;
  country_variants: Record<string, string>;
  expected_fields: string[];
  is_also_submission: boolean;
}

/**
 * Distinctive filename tokens per document type. The single source of truth — `cloud/classify.ts`
 * imports this rather than keeping a rival copy, and `detectDocTypeFromFilename` below scores
 * against it. Two keyword lists for one job is how they drift.
 *
 * Keep these SPECIFIC. A token here is a claim that a filename containing it is probably this
 * type; a vague token ("certificate", "agreement") is a claim that will be wrong.
 */
export const FILENAME_KEYWORDS: Record<string, string[]> = {
  incorporation_cert: ["incorporation", "cr12", "cipc", "cac", "orc", "bipa", "pacra", "status report"],
  tax_clearance: ["tax clearance", "tax compliance", "tcc", "kra", "firs", "nrs", "sars", "gra", "namra", "zra"],
  bank_offer_letter: ["bank", "offer letter", "facility", "term sheet", "financing", "loan"],
  tariff_confirmation: ["tariff", "auction", "reipppp", "afur", "feed-in"],
  land_title: ["certificate of occupancy", "c of o", "title deed", "land title", "freehold"],
  lease_agreement: ["lease", "sublease", "rental", "tenancy"],
  solar_resource_assessment: ["solar resource", "resource assessment", "irradiance", "ghi", "dni", "meteo", "yield"],
  grid_impact_study: ["grid impact", "impact study", "evacuation study", "interconnection study"],
  epc_proposal: ["epc", "engineering", "procurement", "quotation"],
  pv_module_datasheet: ["module datasheet", "pv module", "solar panel", "panel datasheet"],
  inverter_datasheet: ["inverter", "power conversion"],
  eia_certificate: ["eia", "esmp", "environmental", "nema", "dffe", "screening", "fmenv"],
  ppa_agreement: ["ppa", "power purchase", "offtake", "bilateral"],
  grid_connection_agreement: ["grid connection", "connection agreement", "gcca"],
  community_assembly_minutes: ["community", "assembly", "minutes", "consultation", "stakeholder"],
  bee_certificate: ["bee", "b-bbee", "broad-based", "empowerment"],
  generation_license: ["generation licence", "generation license", "epra", "nersa", "mini-grid permit"],
};

/**
 * Guess a document type from its filename.
 *
 * SHARED, because it was copy-pasted into BatchUploadFlow.tsx and DocumentUploadFlow.tsx — and a
 * matcher living in two files is a matcher that gets fixed in one of them.
 *
 * WHAT WAS WRONG. The old version walked DOC_TYPE_FIELD_MAP in insertion order and returned the
 * FIRST type whose key, country-variant word, or any DESCRIPTION word over four characters
 * appeared in the filename. Descriptions are prose. So:
 *
 *   - adding `tripartite_agreement`, whose description contains "community", put it ahead of
 *     `community_assembly_minutes` and swallowed every community-minutes file;
 *   - the tripartite file itself came back `lease_agreement`, because THAT description contains
 *     "agreement";
 *   - `CAC-Status-Report.pdf` came back `grid_impact_study`, because its description contains
 *     "report";
 *   - `Certificate-of-Occupancy-Zaria.pdf` came back `incorporation_cert`, because that variant
 *     contains the word "certificate".
 *
 * All one bug: a weak signal (a word inside a sentence) was allowed to beat a strong one (the
 * document's actual name), purely because it appeared earlier in an object literal.
 *
 * So it now SCORES rather than first-matches, and the score is the length of what matched —
 * a longer, more specific hit is a stronger claim. "certificate of occupancy" (24 chars) beats
 * "certificate" (11). Insertion order breaks ties and nothing else.
 */
export function detectDocTypeFromFilename(fileName: string, countryCode: string): string | null {
  // Normalise separators, and split on ANY non-alphanumeric so punctuation cannot weld itself to
  // a token. The old code split on spaces alone, so renaming the NG tax variant to
  // "…(formerly FIRS) Tax Clearance…" yielded the token "firs)" — which no filename contains,
  // silently breaking detection for exactly the legacy certificates the rename existed to serve.
  const name = ` ${fileName.toLowerCase().replace(/[^a-z0-9]+/gi, " ").trim()} `;
  const has = (needle: string): boolean => {
    const n = needle.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    return n.length > 0 && name.includes(` ${n} `.replace(/\s+/g, " "));
  };

  let best: { key: string; score: number } | null = null;

  for (const [key, m] of Object.entries(DOC_TYPE_FIELD_MAP)) {
    let score = 0;

    // The type's own name. The strongest signal there is.
    const asWords = key.replace(/_/g, " ");
    if (has(asWords)) score = Math.max(score, asWords.length * 3);

    // Curated keywords. Specific by construction; weighted by how much they pin down.
    for (const kw of FILENAME_KEYWORDS[key] ?? []) {
      if (has(kw)) score = Math.max(score, kw.length * 2);
    }

    // The country's own name for the document, whole phrase then distinctive words.
    const variant = m.country_variants[countryCode];
    if (variant) {
      if (has(variant)) score = Math.max(score, variant.length * 2);
      for (const w of variant.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3)) {
        if (has(w)) score = Math.max(score, w.length);
      }
    }

    // Description words are NOT consulted. They are prose, they are why this was broken, and
    // every signal they could give is better given by a keyword above.

    if (score > 0 && (!best || score > best.score)) best = { key, score };
  }

  return best?.key ?? null;
}

export const DOC_TYPE_FIELD_MAP: Record<string, DocTypeMapping> = {
  incorporation_cert: {
    description: "Company registration certificate from national registry",
    country_variants: {
      KE: "Certificate of Incorporation + CR12",
      NG: "CAC Certificate of Incorporation",
      ZA: "CIPC Registration Certificate",
      GH: "ORC Registration Certificate",
      NA: "BIPA Registration Certificate",
      ZM: "PACRA Registration Certificate",
    },
    expected_fields: ["C-001", "C-002", "C-003", "C-004"],
    is_also_submission: true,
  },
  tax_clearance: {
    description: "Tax compliance certificate from revenue authority",
    country_variants: {
      KE: "KRA Tax Compliance Certificate",
      // NOTE: I renamed this to "Nigeria Revenue Service (formerly FIRS)" on 2026-07-14, on my
      // own reading of the 2025 tax acts. That was a regulatory claim I had no standing to make —
      // I have no visibility into the regulatory research or its progress. Reverted, and filed as
      // a QUESTION for the regulatory team in COORDINATION.md. The product does not get to decide
      // what a Nigerian tax authority is called; the research does.
      NG: "FIRS Tax Clearance Certificate",
      ZA: "SARS Tax Clearance PIN",
      GH: "GRA Tax Clearance Certificate",
      NA: "NamRA Tax Compliance Certificate",
      ZM: "ZRA Tax Clearance Certificate",
    },
    expected_fields: ["C-005", "C-006"],
    is_also_submission: true,
  },
  bank_offer_letter: {
    description: "Financing commitment from lender or DFI",
    country_variants: { KE: "Bank Offer Letter", NG: "Bank Offer Letter", ZA: "Facility Letter / Term Sheet", GH: "Bank Offer Letter", NA: "Bank Offer Letter", ZM: "Bank Offer Letter" },
    expected_fields: ["F-011", "F-012", "F-014"],
    is_also_submission: true,
  },
  tariff_confirmation: {
    description: "Regulator tariff approval or auction result",
    country_variants: {
      KE: "EPRA Tariff Approval Letter / Auction Result",
      NG: "NERC Tariff Order / AFUR Tool Output",
      ZA: "REIPPPP Preferred Bidder Confirmation / Wheeling Rate Agreement",
      GH: "PURC Tariff Approval / Feed-in Tariff",
      NA: "ECB Tariff Determination",
      ZM: "ERB Tariff Approval / GET FiT Terms",
    },
    expected_fields: ["F-006", "F-007", "F-008", "F-009"],
    is_also_submission: false,
  },
  land_title: {
    description: "Proof of land ownership or leasehold rights",
    country_variants: {
      KE: "Title Deed / Allotment Letter / Community Land Certificate",
      NG: "Certificate of Occupancy (C of O)",
      ZA: "Title Deed",
      GH: "Lands Commission Certificate / Stool Land Lease",
      NA: "Title Deed / Communal Land Board Consent",
      ZM: "Commissioner of Lands Leasehold / Chief's Consent",
    },
    expected_fields: ["S-009", "S-010", "S-011", "S-012", "S-005"],
    is_also_submission: true,
  },
  lease_agreement: {
    description: "Land lease agreement between developer and landowner",
    country_variants: { KE: "Lease Agreement", NG: "Deed of Assignment / Lease", ZA: "Lease Agreement", GH: "Lease Agreement / Stool Land Concession", NA: "Lease Agreement", ZM: "Lease Agreement" },
    // S-013/014/015 were listed here but have NEVER existed as field_definition rows. An
    // undefined id does not error — extractDocument() falls back to a stub whose *name is the
    // raw id*, so the model was being asked to find a field called "S-013", with no name, no
    // unit and no guidance. It degrades extraction silently. Replaced with the land/tenure
    // fields that are actually defined and that a lease genuinely carries.
    // See doc-types.test.ts, which now makes this a build failure rather than a shrug.
    expected_fields: ["S-005", "S-009", "S-010", "S-011", "S-012"],
    is_also_submission: true,
  },
  solar_resource_assessment: {
    description: "Solar yield study from resource consultant",
    country_variants: { KE: "Solar Resource Assessment", NG: "Solar Resource Assessment", ZA: "Solar Resource Assessment", GH: "Solar Resource Assessment", NA: "Solar Resource Assessment", ZM: "Solar Resource Assessment" },
    // P-017/018/019 have never existed (they were presumably meant to be GHI, performance
    // ratio and uncertainty). P-016 (Annual Solar Yield) is the one real field this document
    // yields; S-001/S-002 are the site coordinates a resource assessment always states.
    expected_fields: ["P-016", "S-001", "S-002"],
    is_also_submission: false,
  },
  grid_impact_study: {
    description: "Grid study report from utility/system operator",
    country_variants: {
      KE: "KPLC/KETRACO Grid Impact Study Report",
      NG: "TCN Evacuation Study Report",
      ZA: "Eskom Cost Estimate Letter (CEL)",
      GH: "GRIDCo Grid Impact Study",
      NA: "NamPower Grid Study",
      ZM: "ZESCO Grid Study Report",
    },
    expected_fields: ["G-003", "G-005", "G-006", "P-022"],
    is_also_submission: false,
  },
  epc_proposal: {
    description: "Engineering procurement construction proposal or contract",
    country_variants: { KE: "EPC Proposal / Contract", NG: "EPC Proposal / Contract", ZA: "EPC Proposal / Contract", GH: "EPC Proposal / Contract", NA: "EPC Proposal / Contract", ZM: "EPC Proposal / Contract" },
    expected_fields: ["F-001", "F-003", "P-004", "P-005", "P-006", "P-009", "P-010"],
    is_also_submission: false,
  },
  pv_module_datasheet: {
    description: "PV module manufacturer technical specification sheet",
    country_variants: { KE: "PV Module Datasheet", NG: "PV Module Datasheet", ZA: "PV Module Datasheet", GH: "PV Module Datasheet", NA: "PV Module Datasheet", ZM: "PV Module Datasheet" },
    // P-007/008/020 have never existed as field_definition rows.
    expected_fields: ["P-005", "P-006"],
    is_also_submission: false,
  },
  inverter_datasheet: {
    description: "Inverter manufacturer technical specification sheet",
    country_variants: { KE: "Inverter Datasheet", NG: "Inverter Datasheet", ZA: "Inverter Datasheet", GH: "Inverter Datasheet", NA: "Inverter Datasheet", ZM: "Inverter Datasheet" },
    // P-011 has never existed as a field_definition row.
    expected_fields: ["P-009", "P-010"],
    is_also_submission: false,
  },
  eia_certificate: {
    description: "Environmental clearance issued by environmental authority",
    country_variants: {
      KE: "NEMA EIA License",
      NG: "FMoE EIA Approval Certificate",
      ZA: "DFFE Environmental Authorisation / Exclusion Norm Registration",
      GH: "EPA Environmental Permit",
      NA: "ECC Environmental Clearance",
      ZM: "ZEMA EIA Certificate",
    },
    expected_fields: ["E-005", "E-006"],
    is_also_submission: true,
  },
  ppa_agreement: {
    description: "Signed Power Purchase Agreement",
    country_variants: {
      KE: "KPLC PPA / Corporate PPA",
      NG: "NBET PPA / Bilateral PPA / DisCo PPA",
      ZA: "Eskom PPA (REIPPPP) / Corporate PPA / Wheeling PPA",
      GH: "ECG PPA / GRIDCo PPA",
      NA: "NamPower PPA",
      ZM: "ZESCO PPA / GET FiT PPA",
    },
    expected_fields: ["F-006", "F-007", "F-008", "F-009", "O-001", "O-004"],
    is_also_submission: true,
  },
  grid_connection_agreement: {
    description: "Signed grid connection and use-of-system agreement",
    country_variants: {
      KE: "KPLC Grid Connection Agreement",
      NG: "TCN Grid Connection Agreement",
      ZA: "Eskom CUSA/CUOSA",
      GH: "GRIDCo Grid Connection Agreement",
      NA: "NamPower Connection Agreement",
      ZM: "ZESCO Grid Connection Agreement",
    },
    expected_fields: ["G-003", "G-006", "G-007", "G-008", "P-022"],
    is_also_submission: true,
  },
  // REMOVED 2026-07-14: `tripartite_agreement`. I added it on my own reading of the Mini-Grid
  // Regulations 2026 — asserting in its description that an interconnected mini-grid REQUIRES one
  // and that it has no effect until registered. Those are regulatory claims, and they are not
  // mine to make. Filed as a question for the regulatory team (COORDINATION.md); it comes back if
  // and when they say it should, with THEIR description, not mine.
  community_assembly_minutes: {
    description: "Record of community consent for land use",
    country_variants: { KE: "Community Assembly Minutes", NG: "Community Engagement Record", ZA: "Community Participation Record", GH: "Community Engagement Record", NA: "Community Assembly Minutes", ZM: "Community Engagement Record" },
    expected_fields: ["CS-002", "CS-003"],
    is_also_submission: true,
  },
  bee_certificate: {
    description: "B-BBEE verification certificate (South Africa only)",
    country_variants: { ZA: "B-BBEE Certificate" },
    expected_fields: ["C-012"],
    is_also_submission: true,
  },
  generation_license: {
    description: "Issued generation license or registration certificate",
    country_variants: {
      KE: "EPRA Generation License",
      NG: "NERC Generation License",
      ZA: "NERSA Registration Certificate",
      GH: "Energy Commission Generation License",
      NA: "ECB Generation Licence",
      ZM: "ERB Generation Licence",
    },
    expected_fields: ["R-001", "R-002", "R-003"],
    is_also_submission: true,
  },
};

/** Get all doc type keys */
export const DOC_TYPES = Object.keys(DOC_TYPE_FIELD_MAP);

/** Get the display name for a doc type in a given country */
export function getDocTypeDisplayName(docType: string, countryCode: string): string {
  const mapping = DOC_TYPE_FIELD_MAP[docType];
  if (!mapping) return docType.replace(/_/g, " ");
  return mapping.country_variants[countryCode] || mapping.description;
}
